import React from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import BusinessCard from '../dashboard/BusinessCard';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';
import { ConnectionType } from '../../types/network';

interface NetworkBusinessCardProps {
  user: {
    id: string;
    name: string;
    profession: string;
    city: string;
    photo?: string;
    businessName?: string;
    businessIndustry?: string;
    businessPhoto?: string;
    industry?: string;
    designation?: string;
    role?: string;
  };
  connectionType?: ConnectionType;
  variant?: 'connection' | 'incoming-request' | 'outgoing-request' | 'search';
  connectionStatus?: 'connected' | 'pending' | 'none';
  isAdmin?: boolean;
  onMessage?: (userId: string) => void;
  onViewProfile?: (userId: string) => void;
  onRemove?: (userId: string) => void;
  onAccept?: (userId: string) => void;
  onReject?: (userId: string) => void;
  onSendRequest?: (userId: string) => void;
  onToggleRole?: (userId: string, currentRole: string) => void;
  onDeactivate?: (userId: string) => void;
  onReactivate?: (userId: string) => void;
}

const NetworkBusinessCard: React.FC<NetworkBusinessCardProps> = ({
  user,
  connectionType,
  variant = 'connection',
  connectionStatus,
  isAdmin = false,
  onMessage,
  onViewProfile,
  onRemove,
  onAccept,
  onReject,
  onSendRequest,
  onToggleRole,
  onDeactivate,
  onReactivate,
}) => {
  const { theme } = useTheme();

  const getConnectionTypeLabel = (type?: ConnectionType): string => {
    if (!type) return '';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const getConnectionTypeColor = (type?: ConnectionType): string => {
    switch (type) {
      case 'mentor': return '#4CAF50';
      case 'client': return '#2196F3';
      case 'supplier': return '#FF9800';
      default: return theme.textTertiary;
    }
  };

  // Format user data for BusinessCard component
  const userData = {
    ...user,
    designation: user.profession,
  };

  // Top right content - connection type badge
  const topRightContent = connectionType ? (
    <View style={[styles.badge, { backgroundColor: getConnectionTypeColor(connectionType) + '20' }]}>
      <ThemedText style={[styles.badgeText, { color: getConnectionTypeColor(connectionType) }]}>
        {getConnectionTypeLabel(connectionType)}
      </ThemedText>
    </View>
  ) : null;

  // Footer content - action buttons based on variant
  const footerContent = (() => {
    if (variant === 'connection' && onMessage && onRemove) {
      // My Network: Message and Remove buttons
      return (
        <View style={styles.footerActions}>
          <TouchableOpacity
            style={[styles.actionButton, { backgroundColor: theme.accent + '15' }]}
            onPress={() => onMessage(user.id)}
            activeOpacity={0.7}
          >
            <Ionicons name="chatbubble-outline" size={18} color={theme.accent} />
            <ThemedText style={[styles.actionButtonText, { color: theme.accent }]}>
              Message
            </ThemedText>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.actionButton, { backgroundColor: 'rgba(244, 67, 54, 0.1)' }]}
            onPress={() => onRemove(user.id)}
            activeOpacity={0.7}
          >
            <Ionicons name="person-remove-outline" size={18} color="#F44336" />
            <ThemedText style={[styles.actionButtonText, { color: '#F44336' }]}>
              Remove
            </ThemedText>
          </TouchableOpacity>
        </View>
      );
    }

    if (variant === 'incoming-request' && onAccept && onReject) {
      // Incoming Requests: Reject and Accept buttons
      return (
        <View style={styles.footerActions}>
          <TouchableOpacity
            style={[styles.actionButton, { backgroundColor: 'rgba(244, 67, 54, 0.1)', borderWidth: 1, borderColor: '#F44336' }]}
            onPress={() => onReject(user.id)}
            activeOpacity={0.7}
          >
            <Ionicons name="close-circle-outline" size={18} color="#F44336" />
            <ThemedText style={[styles.actionButtonText, { color: '#F44336' }]}>
              Reject
            </ThemedText>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.actionButton, { backgroundColor: '#4CAF50' }]}
            onPress={() => onAccept(user.id)}
            activeOpacity={0.7}
          >
            <Ionicons name="checkmark-circle-outline" size={18} color="#FFFFFF" />
            <ThemedText style={[styles.actionButtonText, { color: '#FFFFFF' }]}>
              Accept
            </ThemedText>
          </TouchableOpacity>
        </View>
      );
    }

    if (variant === 'outgoing-request') {
      // Outgoing Requests: Pending status
      return (
        <View style={styles.footerActions}>
          <View style={[styles.actionButton, { backgroundColor: theme.backgroundSecondary }]}>
            <Ionicons name="time-outline" size={18} color={theme.textSecondary} />
            <ThemedText style={[styles.actionButtonText, { color: theme.textSecondary }]}>
              Pending
            </ThemedText>
          </View>
        </View>
      );
    }

    if (variant === 'search' && onSendRequest) {
      // Admin controls for search
      if (isAdmin && onToggleRole && onDeactivate && onReactivate) {
        // If user is deactivated, show reactivate button + message/connect
        if (user.role === 'deactivated') {
          return (
            <View>
              {/* User action buttons */}
              <View style={styles.footerActions}>
                <TouchableOpacity
                  style={[styles.actionButton, { backgroundColor: theme.accent + '15' }]}
                  onPress={() => onMessage?.(user.id)}
                  activeOpacity={0.7}
                >
                  <Ionicons name="chatbubble-outline" size={18} color={theme.accent} />
                  <ThemedText style={[styles.actionButtonText, { color: theme.accent }]}>
                    Message
                  </ThemedText>
                </TouchableOpacity>
                
                {connectionStatus === 'connected' ? (
                  <View style={[styles.actionButton, { backgroundColor: theme.backgroundSecondary }]}>
                    <Ionicons name="checkmark-circle" size={18} color={theme.accent} />
                    <ThemedText style={[styles.actionButtonText, { color: theme.accent }]}>
                      Connected
                    </ThemedText>
                  </View>
                ) : connectionStatus === 'pending' ? (
                  <View style={[styles.actionButton, { backgroundColor: theme.backgroundSecondary }]}>
                    <Ionicons name="time-outline" size={18} color={theme.textSecondary} />
                    <ThemedText style={[styles.actionButtonText, { color: theme.textSecondary }]}>
                      Pending
                    </ThemedText>
                  </View>
                ) : (
                  <TouchableOpacity
                    style={[styles.actionButton, { backgroundColor: theme.accent }]}
                    onPress={() => onSendRequest(user.id)}
                    activeOpacity={0.7}
                  >
                    <Ionicons name="person-add-outline" size={18} color="#FFFFFF" />
                    <ThemedText style={[styles.actionButtonText, { color: '#FFFFFF' }]}>
                      Connect
                    </ThemedText>
                  </TouchableOpacity>
                )}
              </View>
              
              {/* Admin controls */}
              <View style={[styles.adminControls, { marginTop: 8 }]}>
                <TouchableOpacity
                  style={[styles.adminButton, { backgroundColor: '#4CAF50' }]}
                  onPress={() => onReactivate(user.id)}
                  activeOpacity={0.7}
                >
                  <Ionicons name="checkmark-circle-outline" size={18} color="#FFFFFF" />
                  <ThemedText style={[styles.adminButtonText, { color: '#FFFFFF' }]}>
                    Reactivate
                  </ThemedText>
                </TouchableOpacity>
              </View>
            </View>
          );
        }

        // Otherwise show toggle role and deactivate buttons + message/connect
        return (
          <View>
            {/* User action buttons */}
            <View style={styles.footerActions}>
              <TouchableOpacity
                style={[styles.actionButton, { backgroundColor: theme.accent + '15' }]}
                onPress={() => onMessage?.(user.id)}
                activeOpacity={0.7}
              >
                <Ionicons name="chatbubble-outline" size={18} color={theme.accent} />
                <ThemedText style={[styles.actionButtonText, { color: theme.accent }]}>
                  Message
                </ThemedText>
              </TouchableOpacity>
              
              {connectionStatus === 'connected' ? (
                <View style={[styles.actionButton, { backgroundColor: theme.backgroundSecondary }]}>
                  <Ionicons name="checkmark-circle" size={18} color={theme.accent} />
                  <ThemedText style={[styles.actionButtonText, { color: theme.accent }]}>
                    Connected
                  </ThemedText>
                </View>
              ) : connectionStatus === 'pending' ? (
                <View style={[styles.actionButton, { backgroundColor: theme.backgroundSecondary }]}>
                  <Ionicons name="time-outline" size={18} color={theme.textSecondary} />
                  <ThemedText style={[styles.actionButtonText, { color: theme.textSecondary }]}>
                    Pending
                  </ThemedText>
                </View>
              ) : (
                <TouchableOpacity
                  style={[styles.actionButton, { backgroundColor: theme.accent }]}
                  onPress={() => onSendRequest(user.id)}
                  activeOpacity={0.7}
                >
                  <Ionicons name="person-add-outline" size={18} color="#FFFFFF" />
                  <ThemedText style={[styles.actionButtonText, { color: '#FFFFFF' }]}>
                    Connect
                  </ThemedText>
                </TouchableOpacity>
              )}
            </View>
            
            {/* Admin controls */}
            <View style={[styles.adminControls, { marginTop: 8 }]}>
              <TouchableOpacity
                style={[styles.adminButton, { backgroundColor: '#FF9800' }]}
                onPress={() => onToggleRole(user.id, user.role || 'user')}
                activeOpacity={0.7}
              >
                <Ionicons 
                  name={user.role === 'admin' ? "person-outline" : "shield-outline"} 
                  size={18} 
                  color="#FFFFFF" 
                />
                <ThemedText style={[styles.adminButtonText, { color: '#FFFFFF' }]}>
                  {user.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                </ThemedText>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.adminButton, { backgroundColor: '#F44336' }]}
                onPress={() => onDeactivate(user.id)}
                activeOpacity={0.7}
              >
                <Ionicons name="ban-outline" size={18} color="#FFFFFF" />
                <ThemedText style={[styles.adminButtonText, { color: '#FFFFFF' }]}>
                  Deactivate
                </ThemedText>
              </TouchableOpacity>
            </View>
          </View>
        );
      }

      // Search: Connect button based on connection status
      if (connectionStatus === 'connected') {
        return (
          <View style={styles.footerActions}>
            <TouchableOpacity
              style={[styles.actionButton, { backgroundColor: theme.accent + '15' }]}
              onPress={() => onMessage?.(user.id)}
              activeOpacity={0.7}
            >
              <Ionicons name="chatbubble-outline" size={18} color={theme.accent} />
              <ThemedText style={[styles.actionButtonText, { color: theme.accent }]}>
                Message
              </ThemedText>
            </TouchableOpacity>
            
            <View style={[styles.actionButton, { backgroundColor: theme.backgroundSecondary }]}>
              <Ionicons name="checkmark-circle" size={18} color={theme.accent} />
              <ThemedText style={[styles.actionButtonText, { color: theme.accent }]}>
                Connected
              </ThemedText>
            </View>
          </View>
        );
      }

      if (connectionStatus === 'pending') {
        return (
          <View style={styles.footerActions}>
            <TouchableOpacity
              style={[styles.actionButton, { backgroundColor: theme.accent + '15' }]}
              onPress={() => onMessage?.(user.id)}
              activeOpacity={0.7}
            >
              <Ionicons name="chatbubble-outline" size={18} color={theme.accent} />
              <ThemedText style={[styles.actionButtonText, { color: theme.accent }]}>
                Message
              </ThemedText>
            </TouchableOpacity>
            
            <View style={[styles.actionButton, { backgroundColor: theme.backgroundSecondary }]}>
              <Ionicons name="time-outline" size={18} color={theme.textSecondary} />
              <ThemedText style={[styles.actionButtonText, { color: theme.textSecondary }]}>
                Pending
              </ThemedText>
            </View>
          </View>
        );
      }

      // None - show connect button and chat button
      return (
        <View style={styles.footerActions}>
          <TouchableOpacity
            style={[styles.actionButton, { backgroundColor: theme.accent + '15' }]}
            onPress={() => onMessage?.(user.id)}
            activeOpacity={0.7}
          >
            <Ionicons name="chatbubble-outline" size={18} color={theme.accent} />
            <ThemedText style={[styles.actionButtonText, { color: theme.accent }]}>
              Message
            </ThemedText>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[styles.actionButton, { backgroundColor: theme.accent }]}
            onPress={() => onSendRequest(user.id)}
            activeOpacity={0.7}
          >
            <Ionicons name="person-add-outline" size={18} color="#FFFFFF" />
            <ThemedText style={[styles.actionButtonText, { color: '#FFFFFF' }]}>
              Connect
            </ThemedText>
          </TouchableOpacity>
        </View>
      );
    }

    return null;
  })();

  return (
    <View style={styles.container}>
      <BusinessCard
        userData={userData}
        topRightContent={topRightContent}
        footerContent={footerContent}
        onPress={onViewProfile ? () => onViewProfile(user.id) : undefined}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  badge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    alignSelf: 'flex-start',
  },
  badgeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  footerActions: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 16,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 8,
    gap: 6,
  },
  actionButtonText: {
    fontSize: 13,
    fontWeight: '600',
  },
  adminControls: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 16,
  },
  adminButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 8,
    gap: 6,
  },
  adminButtonText: {
    fontSize: 13,
    fontWeight: '600',
  },
});

export default NetworkBusinessCard;
